import _ from 'lodash'
import { FTProcess } from '@/models/FTProcess';
import { weightToLength, initProcess, ProcessConfig, ConversionResults, ParsedVariables } from './index'
import { FTProcessRow } from '@/models/FTProcessRow';
import { ConversionDetails } from '@/models/ConversionDetails';
import { convertStep } from './CConvertStep';
import { compareStringIgnoreCase } from '@/utils';

export function loadConfig({ process, processRow }: ParsedVariables): ProcessConfig {
  return {
    settings: {
      laminationEnabled: {
        type: 'checkbox',
        name: 'Enable Lamination',
      },
      cuttingEnabled: {
        type: 'checkbox',
        name: 'Enable Cutting',
        default: true
      },
      horizontalGap: {
        type: 'text',
        name: 'Horizontal Gap Between Labels',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: _.get(process, 'settings.labelGap', 5)
      },
      verticalGap: {
        type: 'text',
        name: 'Vertical Gap Between Labels',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: _.get(process, 'settings.labelGap', 5)
      },
      printingEnabled: {
        type: 'checkbox',
        name: 'Enable Printing',
        default: true
      },
      labelWidth: {
        type: 'text',
        name: 'Width',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: _.get(process, 'ftFactoryTicket.order.product.size.WIDTH')
      },
      labelRepeat: {
        type: 'text',
        name: 'Repeat',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: _.get(process, 'ftFactoryTicket.order.product.size.REPEAT')
      }
    },
    inputs: {
      goods: [
        {
          goodsType: 'layer',
          goodsFilter: {
            form: ['roll'],
            family: ['PSL']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Customer Waste %',
              type: 'text',
              value: 'customerWaste',
              unit: '%',
              disablePrint: true,
              disabled: false
            },
            {
              name: 'Running Meters',
              type: 'label',
              value: _.get(process, 'conversionDetails.linearMeters', ''),
              unit: 'm',
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              enableConvertFnIf: compareStringIgnoreCase(_.get(processRow, 'goodsObject.rawMaterialUnit', ''),'SQM'),
              disabled: false
            }
          ]
        },
        {
          goodsType: 'ink',
          goodsFilter: {
            type: ['ORM'],
            family: ['Chemical'],
            category: ['Ink']
          },
          multiple: true,
          showIf: _.get(process, 'settings.printingEnabled') === true,
          addMultipleButtonText: 'add more inks',
          params: [
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'adhesive',
          goodsFilter: {
            type: ['ORM'],
            family: ['Chemical'],
            category: ['Adhesive']
          },
          multiple: false,
          showIf: _.get(process, 'settings.laminationEnabled') === true,
          params: [
            {
              name: 'Linear Length',
              type: 'label',
              value: false,
              disablePrint: true,
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'lamination',
          goodsFilter: {
            form: ['roll'],
            type: ['MRM'],
            family: ['Film']
          },
          multiple: false,
          showIf: _.get(process, 'settings.laminationEnabled') === true,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'single',
      arguments: [
        {
          name: 'Printing Side',
          type: 'dropdown',
          value: 'printingSide',
          optionsArray: [ 'Surface Side', 'Reverse Side'],
          disabled: false
        },
        {
          name: 'Winding Direction',
          type: 'dropdown',
          value: 'direction',
          optionsArray: [ 'C1', 'C2', 'C3', 'C4' ],
          showIf: !_.get(processRow, 'ftProcess.ftFactoryTicket.order.product.printing.windingDirection', false),
          disabled: false
        },
        {
          name: 'Winding Direction',
          type: 'label',
          value: _.get(processRow, 'ftProcess.ftFactoryTicket.order.product.printing.windingDirection', ''),
          showIf: _.get(processRow, 'ftProcess.ftFactoryTicket.order.product.printing.windingDirection', false),
          disabled: true
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'Linear Length',
        value: 'linearMeters'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);

  let linearMeters = 0, netWeight = 0, totalAreaInM2 = 0;
  const layerInputProcessRow = process.inputs[0]
  let outputFTProcessRow = process.outputs[0];
  conversionResult.outputs[0] = outputFTProcessRow;

  if (layerInputProcessRow.goodsType == 'layer' && _.get(layerInputProcessRow, 'goodsObject.dimensions')) {
    linearMeters = weightToLength(layerInputProcessRow.quantity, layerInputProcessRow.goodsObject.density, _.get(layerInputProcessRow,'goodsObject.dimensions.width'));
    outputFTProcessRow.goodsObject.copyFrom(layerInputProcessRow.goodsObject);
    totalAreaInM2 = (linearMeters * _.get(layerInputProcessRow,'goodsObject.dimensions.width'))/1000;
  }
  if(process.settings.laminationEnabled) {
    const adhesiveIndex =_.findIndex(process.inputs, { goodsType: 'adhesive' });
    const laminationIndex =_.findIndex(process.inputs, { goodsType: 'lamination' });
    if(adhesiveIndex > -1 && laminationIndex > -1) {
      const adhesiveInputProcessRow = process.inputs[adhesiveIndex];
      const laminationInputProcessRow = process.inputs[laminationIndex];
      outputFTProcessRow = convertStep.lamination(layerInputProcessRow, laminationInputProcessRow, adhesiveInputProcessRow);
      outputFTProcessRow.goodsObject.form = 'roll';
    }
  }
  _.mapValues(process.inputs, (processInputRow: FTProcessRow) => {
    netWeight += (processInputRow.quantity || 0);
  });
  if(process.settings.cuttingEnabled && outputFTProcessRow.goodsObject.dimensions && layerInputProcessRow.customerWaste) {
    const labelWidthWithGap = process.settings.labelWidth + process.settings.horizontalGap;
    const labelLengthWithGap = process.settings.labelRepeat + process.settings.verticalGap;
    const noOfLabels = totalAreaInM2/((labelLengthWithGap * labelWidthWithGap) / 1000000);
    const removedAreaInM2 = (layerInputProcessRow.customerWaste/100) * totalAreaInM2;
    const backGsm = layerInputProcessRow.goodsObject.psl.linerGsm;
    const frontGsm = outputFTProcessRow.goodsObject.density - backGsm;
    outputFTProcessRow.quantity = (frontGsm*(totalAreaInM2 - removedAreaInM2) + backGsm * totalAreaInM2)/1000;
    outputFTProcessRow.goodsObject.density = _.round((outputFTProcessRow.quantity * 1000)/totalAreaInM2, 2);
    const labelsPerKg = noOfLabels / outputFTProcessRow.quantity;
    outputFTProcessRow.goodsObject.setPerKgRatio('plA', labelsPerKg)
  } else {
    outputFTProcessRow.quantity = netWeight;
  }
  conversionResult.details.totalQuantity = netWeight;
  conversionResult.details.linearMeters = linearMeters;
  conversionResult.details.wastePercentage = ((netWeight - outputFTProcessRow.quantity) / netWeight) * 100;
  conversionResult.details.waste = netWeight - outputFTProcessRow.quantity;
  conversionResult.details.netWeight = outputFTProcessRow.quantity;
  conversionResult.details.kgPerHour = 60 * (process.machineDetail.mechanicalSpeed / linearMeters) * conversionResult.details.totalQuantity;
  conversionResult.details.plannedHours = conversionResult.details.totalQuantity / conversionResult.details.kgPerHour;

  return conversionResult;
}